/**
 * Wspólne mapowanie wyboru z ConfigContext na identyfikatory ERP.
 *
 * Ta sama logika musi być użyta przy wycenie (żądanie do
 * /letter-configuration-price) i przy budowaniu erpData zapisywanego
 * w konfiguracji — inaczej wycena w konfiguratorze i przeliczenie
 * zamówienia po stronie sklepu trafią w różne wiersze cennika.
 *
 * Zgodne z konfiguratorem osadzonym na karcie produktu w motywie
 * (resources/scripts/components/LetterConfigurator/constants.js
 * i MountingTypeSelector.jsx).
 */

/** ID koloru (term WooCommerce), dla którego wybiera się wykończenie (mat / poler). */
export const COLORS_WITH_TYPE = [196];

/** Wykończenie „kolor" — używane, gdy kolor nie ma wyboru wykończenia. */
export const DEFAULT_TYPE_ERP_ID = 3;

/** Domyślny rozmiar interpunkcji w ERP, gdy wycena nie zwróciła innego. */
export const DEFAULT_PUNCTUATION_SIZE_ERP_ID = 11;

/** Opcje mocowania widoczne w toolbarze (klucz → etykieta z configData.mounting). */
export const MOUNTING_OPTIONS = [
    { key: 'kolek', label: 'Bolec' },
    { key: 'tasma', label: 'Taśma' },
];

/** Tryby z pola ACF `mounting_type` produktu, w których selector jest ukryty. */
const MOUNTING_TYPES_WITHOUT_SELECTOR = ['kolki_only', 'tasma_only'];

/** Dopasowanie klucza selectora do wpisu w fontsData.mounting (po slugu lub nazwie). */
const MOUNTING_MATCHERS = {
    kolek: { slugs: ['b', 'bolec', 'kolek'], nameHints: ['bolec', 'kolek', 'kołek'] },
    tasma: { slugs: ['t', 'tasma'], nameHints: ['taśma', 'tasma', '3m'] },
};

/** Rozmiary liter (cm / mm), dla których taśma 3M jest niedostępna. */
const TAPE_RESTRICTED_CM = [2, 2.5];
const TAPE_RESTRICTED_MM = [20, 25];

/**
 * @param {Object} config - stan ConfigContext
 * @returns {{typeErpId: number, colorErpId: number|null}}
 */
export function resolveFinishAndColor(config) {
    const colorId = parseInt(config?.selectedColorObject?.id, 10);
    const hasType = COLORS_WITH_TYPE.includes(colorId);

    const typeErpId = hasType
        ? (parseInt(config?.selectedTypeObject?.id_erp, 10) || DEFAULT_TYPE_ERP_ID)
        : DEFAULT_TYPE_ERP_ID;

    // Kolor ma znaczenie w cenniku tylko dla wykończenia „kolor".
    const colorErpId = typeErpId === DEFAULT_TYPE_ERP_ID
        ? (parseInt(config?.selectedColorObject?.id_erp, 10) || null)
        : null;

    return { typeErpId, colorErpId };
}

/**
 * Rozmiar interpunkcji dobrany przez serwer (cennik „Int." zależy od materiału).
 *
 * @param {Object|null} pricing - odpowiedź /letter-configuration-price
 * @returns {number}
 */
export function resolvePunctuationSizeErpId(pricing) {
    const punctuation = (pricing?.items || []).find((item) => item.kind === 'punctuation');
    return parseInt(punctuation?.sizeErpId, 10) || DEFAULT_PUNCTUATION_SIZE_ERP_ID;
}

/**
 * Wpis mocowania z danych czcionki odpowiadający kluczowi selectora.
 * Gdy nie ma dopasowania — pierwszy dostępny (zachowanie sprzed selectora).
 *
 * @param {Array} mountings - fontsData.mounting
 * @param {string} key - 'kolek' | 'tasma'
 * @returns {Object|null}
 */
export function findMountingOption(mountings, key) {
    const list = Array.isArray(mountings) ? mountings : [];
    const matcher = MOUNTING_MATCHERS[key];

    if (matcher) {
        const match = list.find((m) => {
            const name = String(m?.name || '').toLowerCase();
            const slug = String(m?.slug || '').toLowerCase();
            return matcher.slugs.includes(slug) || matcher.nameHints.some((hint) => name.includes(hint));
        });
        if (match) {
            return match;
        }
    }

    return list[0] || null;
}

/** Czy produkt pozwala wybrać mocowanie (bolec / taśma). */
export function isMountingSelectable(mountingType) {
    return !MOUNTING_TYPES_WITHOUT_SELECTOR.includes(mountingType || 'kolki_only');
}

/**
 * Czy rozmiar liter to 2 cm (20 mm) lub 2,5 cm (25 mm) — taśma 3M niedostępna.
 * Sprawdza nazwę i slug, bo format danych nie jest gwarantowany.
 *
 * @param {Object|null} fontSize - obiekt { id, name, slug }
 */
export function isTapeRestrictedSize(fontSize) {
    if (!fontSize) return false;

    const name = String(fontSize.name || '').toLowerCase().replace(',', '.');
    const slug = String(fontSize.slug || '').toLowerCase().replace(',', '.');

    const cmMatch = name.match(/(\d+(?:\.\d+)?)\s*cm/);
    if (cmMatch && TAPE_RESTRICTED_CM.includes(parseFloat(cmMatch[1]))) {
        return true;
    }

    const haystack = `${name} ${slug}`.replace(/[\s_-]+/g, '');
    return TAPE_RESTRICTED_MM.some((mm) => haystack.includes(`${mm}mm`));
}

/** Obiekt font_size sekcji (po id_erp, potem po nazwie), z fallbackiem do rozmiaru globalnego. */
function findSectionFontSize(section, config) {
    const sizes = Array.isArray(config?.fontsSizesList) ? config.fontsSizesList : [];
    const byErp = section?.fontSizeErpId
        ? sizes.find((s) => String(s?.font_size?.id_erp) === String(section.fontSizeErpId))
        : null;
    const byName = !byErp && section?.fontSize
        ? sizes.find((s) => s?.font_size?.name === section.fontSize)
        : null;

    return (byErp || byName)?.font_size || config?.selectedFontSizeObject || null;
}

/** Czy którakolwiek sekcja ma rozmiar blokujący taśmę 3M. */
export function hasTapeRestrictedSize(sections, config) {
    return Array.isArray(sections)
        && sections.some((section) => isTapeRestrictedSize(findSectionFontSize(section, config)));
}

/**
 * Efektywne mocowanie konfiguracji: respektuje tryb produktu (`mounting_type`),
 * wybór użytkownika i blokadę taśmy dla małych rozmiarów.
 *
 * @param {Object} config - stan ConfigContext
 * @param {Array} [sections] - sekcje tekstu
 * @returns {{key: string, restricted: boolean, mountingType: string, option: Object|null}}
 */
export function resolveMounting(config, sections = []) {
    const mountingType = config?.mountingType || 'kolki_only';
    const restricted = hasTapeRestrictedSize(sections, config);

    let key = config?.selectedMountingKey || (mountingType === 'tasma_only' ? 'tasma' : 'kolek');

    if (mountingType === 'kolki_only') {
        key = 'kolek';
    } else if (mountingType === 'tasma_only') {
        key = 'tasma';
    }

    if (restricted && key === 'tasma') {
        key = 'kolek';
    }

    return {
        key,
        restricted,
        mountingType,
        option: findMountingOption(config?.mountingList, key),
    };
}

function isColorSteel(color) {
    const slug = String(color?.slug || '').toLowerCase();
    return slug === 'srebrny' || slug.includes('stal');
}

/**
 * Czy wybrane mocowanie kieruje do indywidualnej wyceny (zamiast koszyka) —
 * reguły z pola ACF `mounting_type`, identyczne jak na karcie produktu.
 *
 * @param {Object} config - stan ConfigContext
 * @param {string} mountingKey - 'kolek' | 'tasma'
 */
export function mountingRequiresInquiry(config, mountingKey) {
    const mountingType = config?.mountingType || 'kolki_only';

    if (mountingType === 'zamowienie') {
        return true;
    }

    if (mountingKey !== 'tasma') {
        return false;
    }

    if (mountingType === 'kolki_tasma_zapytanie') {
        return true;
    }

    if (mountingType === 'kolki_tasma_stal') {
        return !isColorSteel(config?.selectedColorObject);
    }

    return false;
}
